/**
 * FC.03 — MatchingEngine
 *
 * Motor de matching inteligente que cruza infraestructuras PTEL contra
 * candidatos de referencia usando scoring compuesto multi-criterio:
 *   - Similitud nombre (uFuzzy): 0-40 pts
 *   - Proximidad espacial (Flatbush): 0-30 pts
 *   - Coincidencia tipológica: 0-20 pts
 *   - Bonus catastral (reservado FC.06): 0-10 pts
 *
 * @module services/cotejo/MatchingEngine
 * @version 1.0.0
 */

import uFuzzy from '@leeoniya/ufuzzy';
import Flatbush from 'flatbush';
import type { ProcessedInfrastructure } from '../../types/processing';
import { InfrastructureType } from '../../types/infrastructure';
import type {
  CandidatoReferencia,
  MatchScore,
  MatchConfirmado,
  InfraAusente as _InfraAusente,
  InfraSobrante as _InfraSobrante,
  OpcionesCotejo,
  ReferenciaNormativa as _ReferenciaNormativa,
} from './types';
import { MATCH_THRESHOLDS } from './types';
import { crearUFuzzyConGuarda, normalizeForSearch, type UFuzzyConGuarda } from '../../lib/fuzzySearch';

// =============================================================================
// CONSTANTES
// =============================================================================

/** Pesos scoring */
const W_NOMBRE = 40;
const W_DISTANCIA = 30;
const W_TIPOLOGIA = 20;
const W_CATASTRAL = 10;

/**
 * Tabla de compatibilidad uso DGC (Catastro) → tipologías PTEL (FC.06a).
 *
 * **Es la inversa de `tipologiaACodigoCatastro`** (`types/catastroR2.ts`), que
 * `FUS1` reescribió contra el dato. Reescrita en `UCC1` (02-ago-2026) porque las
 * 14 entradas previas leían la letra como **inicial mnemotécnica** en vez de como
 * el código de la DGC: `A`→HIDRAULICO por «Agua», `M`→MUNICIPAL, `G`→gobierno.
 * Medido sobre el volcado R2 de Colomera (INE 18051, n=6 876 parcelas), la DGC
 * dice otra cosa: `Y`=Sanitario (la tabla ponía DEPORTIVO), `K`=Deportivo (ponía
 * MUNICIPAL), `P`=Público (ponía SANITARIO), `V`=**Vivienda** (ponía DEPORTIVO).
 *
 * El daño no era inerte: `scoreCatastral` puntúa **0 al incompatible**, así que
 * una entrada mal puesta **penaliza al candidato correcto**. En el corpus lo hacía
 * en 3 de 8 puntos —Ayuntamiento, Policía Local y Guardia Civil, los tres sobre
 * parcela `P`— restando 10 sobre 100 con los cortes en 60 y 40.
 *
 * **Procedencia declarada por entrada, en los tres niveles de `FUS1`.**
 *
 * ⚠️ **Un código ausente puntúa neutro (5); una entrada con `[]` puntuaría 0 a
 * todo**, porque un array vacío es *truthy* y no entra por la guarda de
 * `scoreCatastral`. Las letras sin tipología PTEL que las reclame —`A` almacén,
 * `B` almacén agrario, `C` comercial, `G` turismo, `M` suelo sin edificar,
 * `V` vivienda, `Z` agrario— se quedan **fuera de la tabla a propósito**
 * (`DOP-UCC1-DESCONOCIDO`=(a)): abstenerse, no penalizar. Misma trampa de
 * contrato que `FUS1` documentó en `search()`.
 *
 * `MAREMOTO` no aparece: un punto de encuentro no es una edificación.
 */
const USO_COD_COMPATIBLE: Record<string, InfrastructureType[]> = {
  // ── MEDIDO · corpus Colomera n=8 ──────────────────────────────────────────
  /** `E` = Educativo en el CAT (destino «Ed. Básica»). 3/3 centros a 15-27 m.
   *  `CULTURAL` porque la etiqueta DGC del código cubre ambos. */
  E: [InfrastructureType.EDUCATION, InfrastructureType.CULTURAL],
  /** `P` = Público. Ayuntamiento, Policía Local y Guardia Civil atan aquí a ≤19 m,
   *  y el consultorio también (edificio de 2 141 m², no `Y`). Las cinco últimas
   *  llegan por EXCLUSION: la DGC no tiene código propio y `FUS1` les asignó `P`
   *  como la letra más estrecha defendible. */
  P: [
    InfrastructureType.MUNICIPAL, InfrastructureType.POLICE,
    InfrastructureType.HEALTH, InfrastructureType.SOCIAL,
    InfrastructureType.FIRE, InfrastructureType.EMERGENCY,
    InfrastructureType.TELECOM, InfrastructureType.VIAL, InfrastructureType.PORT,
  ],

  // ── TABLA · identidad con la etiqueta DGC medida en el volcado ─────────────
  /** `Y` = Sanitario. `EMERGENCY` por beneficencia (EXCLUSION heredada). */
  Y: [InfrastructureType.HEALTH, InfrastructureType.SOCIAL, InfrastructureType.EMERGENCY],
  /** `K` = Deportivo. */
  K: [InfrastructureType.SPORTS],
  /** `R` = Religioso. `CULTURAL` incluido a propósito: aguas arriba
   *  `mapTypeToTipologia` funde CULTURAL y RELIGIOSO en `PATRIMONIO`. */
  R: [InfrastructureType.RELIGIOUS, InfrastructureType.CULTURAL],
  /** `T` = Espectáculos. */
  T: [InfrastructureType.CULTURAL],
  /** `O` = Oficinas. */
  O: [InfrastructureType.MUNICIPAL],
  /** `I` = Industrial. Las siete últimas por EXCLUSION: sin código DGC propio,
   *  `FUS1` las ató a `I` (depósitos, EDAR, subestaciones, naves, vertederos). */
  I: [
    InfrastructureType.INDUSTRIAL, InfrastructureType.ENERGY,
    InfrastructureType.FUEL, InfrastructureType.HYDRAULIC,
    InfrastructureType.TELECOM, InfrastructureType.RSU,
    InfrastructureType.FIRE, InfrastructureType.PORT,
  ],
  /** `J` = industrial agrario (n=9 en el corpus, sin etiqueta en el volcado). */
  J: [InfrastructureType.INDUSTRIAL, InfrastructureType.RSU],
};

/** Radio máximo por defecto (metros, UTM) */
const DEFAULT_RADIO_M = 500;

/** uFuzzy tolerante para nombres españoles de infraestructuras */
const UFUZZY_OPTS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
  interIns: 6,
};

// =============================================================================
// RESULTADO DEL MATCHING
// =============================================================================

export interface MatchingResult {
  readonly matches: MatchConfirmado[];
  readonly ausentes: CandidatoReferencia[];
  readonly sobrantes: ProcessedInfrastructure[];
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class MatchingEngine {
  /**
   * UFDIRECT R4: buscador con la guarda de agujas largas (más de 4 términos, sin variantes por
   * errata). Sin ella, un par de nombres largos que comparten el arranque bloqueaba P3
   * (7,6 s un solo par, medido el 30-oct-2026); calibrado en P3: 255 = 255 aciertos.
   */
  private readonly uf: UFuzzyConGuarda = crearUFuzzyConGuarda(UFUZZY_OPTS);

  /**
   * Cruza infraestructuras PTEL contra candidatos de referencia.
   *
   * Algoritmo:
   * 1. Construye índice espacial Flatbush con los candidatos
   * 2. Para cada infra PTEL con coordenadas, busca candidatos cercanos
   * 3. Calcula score compuesto para cada par (PTEL ↔ candidato)
   * 4. Asigna mejor match greedy (cada candidato solo se asigna una vez)
   * 5. Candidatos sin match → ausentes; PTEL sin match → sobrantes
   */
  match(
    infrasPTEL: ProcessedInfrastructure[],
    candidatos: CandidatoReferencia[],
    opciones?: Partial<OpcionesCotejo>,
  ): MatchingResult {
    const radioMax = opciones?.radioMaximoMetros ?? DEFAULT_RADIO_M;
    const umbralConfirmado = opciones?.umbralConfirmado ?? MATCH_THRESHOLDS.CONFIRMADO;
    const umbralProbable = opciones?.umbralProbable ?? MATCH_THRESHOLDS.PROBABLE;

    if (candidatos.length === 0) {
      return { matches: [], ausentes: [], sobrantes: [...infrasPTEL] };
    }

    // --- Índice espacial de candidatos ---
    const spatialIdx = this.buildSpatialIndex(candidatos);

    // --- Haystack fuzzy de candidatos ---
    const haystack = candidatos.map(c => normalizeForSearch(c.nombre));

    // --- Scoring para cada par PTEL ↔ candidatos cercanos ---
    const allPairs: Array<{
      ptelIdx: number;
      candIdx: number;
      score: MatchScore;
    }> = [];

    for (let pi = 0; pi < infrasPTEL.length; pi++) {
      const infra = infrasPTEL[pi];
      const x = infra.xFinal;
      const y = infra.yFinal;

      // Candidatos cercanos (espacial)
      const nearbyIdxs = (x != null && y != null)
        ? this.searchRadius(spatialIdx, candidatos, x, y, radioMax)
        : [...candidatos.keys()]; // sin coords → comparar con todos

      for (const ci of nearbyIdxs) {
        const cand = candidatos[ci];
        const score = this.computeScore(infra, cand, x, y, haystack[ci]);
        if (score.total >= umbralProbable) {
          allPairs.push({ ptelIdx: pi, candIdx: ci, score });
        }
      }
    }

    // --- Asignación greedy: mejor score primero, sin repetir ---
    allPairs.sort((a, b) => b.score.total - a.score.total);

    const matchedPTEL = new Set<number>();
    const matchedCand = new Set<number>();
    const matches: MatchConfirmado[] = [];

    for (const pair of allPairs) {
      if (matchedPTEL.has(pair.ptelIdx) || matchedCand.has(pair.candIdx)) continue;

      matchedPTEL.add(pair.ptelIdx);
      matchedCand.add(pair.candIdx);

      matches.push({
        infraPTEL: infrasPTEL[pair.ptelIdx],
        candidato: candidatos[pair.candIdx],
        score: pair.score,
        estado: pair.score.total >= umbralConfirmado ? 'incluido' : 'probable',
      });
    }

    // --- Ausentes y sobrantes ---
    const ausentes = candidatos.filter((_, i) => !matchedCand.has(i));
    const sobrantes = infrasPTEL.filter((_, i) => !matchedPTEL.has(i));

    return { matches, ausentes, sobrantes };
  }

  // ===========================================================================
  // SCORING COMPUESTO
  // ===========================================================================

  private computeScore(
    infra: ProcessedInfrastructure,
    cand: CandidatoReferencia,
    ptelX: number | undefined,
    ptelY: number | undefined,
    candNormalized: string,
  ): MatchScore {
    const nombre = this.scoreNombre(infra.nombre, candNormalized);
    const { score: distancia, metros: distanciaMetros } =
      this.scoreDistancia(ptelX, ptelY, cand.x25830, cand.y25830);
    const tipologia = this.scoreTipologia(infra.tipo, cand.tipologia);
    const catastral = this.scoreCatastral(infra, cand);

    return {
      total: Math.round(nombre + distancia + tipologia + catastral),
      nombre: Math.round(nombre),
      distancia: Math.round(distancia),
      tipologia: Math.round(tipologia),
      catastral,
      distanciaMetros,
    };
  }

  /**
   * Score nombre 0-40: uFuzzy search del nombre PTEL en el haystack del candidato.
   * Si no hay match fuzzy, intenta match parcial por inclusión de tokens.
   */
  private scoreNombre(nombrePTEL: string, candNormalized: string): number {
    const ptelNorm = normalizeForSearch(nombrePTEL);
    if (!ptelNorm || !candNormalized) return 0;

    // Match exacto normalizado
    if (ptelNorm === candNormalized) return W_NOMBRE;

    // uFuzzy: buscar ptelNorm en un haystack de un solo elemento
    const [idxs, _info, _order] = this.uf.search([candNormalized], ptelNorm);

    if (idxs && idxs.length > 0) {
      // uFuzzy encontró match — convertir a 0-40
      // info contiene quality data; si hay order, el ranking es por calidad
      // Para un solo candidato, un hit = buen match
      // Usamos heurística: longitud coincidente / longitud total
      const lenRatio = Math.min(ptelNorm.length, candNormalized.length) /
        Math.max(ptelNorm.length, candNormalized.length);
      return W_NOMBRE * Math.max(0.5, lenRatio);
    }

    // Fallback: tokens compartidos
    const ptelTokens = new Set(ptelNorm.split(' ').filter(t => t.length > 2));
    const candTokens = new Set(candNormalized.split(' ').filter(t => t.length > 2));
    if (ptelTokens.size === 0 || candTokens.size === 0) return 0;

    let shared = 0;
    for (const t of ptelTokens) {
      if (candTokens.has(t)) shared++;
    }

    const tokenRatio = shared / Math.max(ptelTokens.size, candTokens.size);
    return W_NOMBRE * tokenRatio * 0.7; // 70% del máximo para token match
  }

  /**
   * Score distancia 0-30: inversamente proporcional a la distancia en metros.
   * 0 m → 30 pts, radioMax → 0 pts, sin coords → 0 pts.
   */
  private scoreDistancia(
    ptelX: number | undefined, ptelY: number | undefined,
    candX: number, candY: number,
  ): { score: number; metros: number } {
    if (ptelX == null || ptelY == null) {
      return { score: 0, metros: Infinity };
    }

    const dx = ptelX - candX;
    const dy = ptelY - candY;
    const metros = Math.sqrt(dx * dx + dy * dy);

    if (metros > DEFAULT_RADIO_M) {
      return { score: 0, metros };
    }

    // Lineal inversa: 0m = 30pts, 500m = 0pts
    const ratio = 1 - metros / DEFAULT_RADIO_M;
    return { score: W_DISTANCIA * Math.max(0, ratio), metros };
  }

  /**
   * Score tipología 0-20: compara tipo string del PTEL vs InfrastructureType del candidato.
   * Match exacto = 20, mismo grupo = 10, diferente = 0.
   */
  private scoreTipologia(tipoPTEL: string, tipoCandidato: InfrastructureType): number {
    const ptelNorm = normalizeForSearch(tipoPTEL);
    const candNorm = normalizeForSearch(tipoCandidato);

    if (!ptelNorm || !candNorm) return 0;

    // Match exacto
    if (ptelNorm === candNorm) return W_TIPOLOGIA;

    // Grupos de tipologías relacionadas
    const GRUPOS: string[][] = [
      ['health', 'sanitario', 'salud', 'centro salud', 'hospital', 'consultorio'],
      ['education', 'educativo', 'colegio', 'instituto', 'ceip', 'ies'],
      ['police', 'fire', 'seguridad', 'policia', 'bomberos', 'guardia civil', 'proteccion civil'],
      ['hydraulic', 'abastecimiento', 'saneamiento', 'agua', 'deposito', 'edar', 'etap'],
      ['energy', 'energia', 'electrica', 'subestacion'],
      ['cultural', 'patrimonio', 'museo', 'biblioteca', 'religious'],
      ['sports', 'deportivo', 'polideportivo', 'piscina', 'pabellon'],
      ['rsu', 'residuos', 'punto limpio', 'vertedero'],
    ];

    for (const grupo of GRUPOS) {
      const ptelInGrupo = grupo.some(g => ptelNorm.includes(g));
      const candInGrupo = grupo.some(g => candNorm.includes(g));
      if (ptelInGrupo && candInGrupo) return W_TIPOLOGIA * 0.5; // 10 pts
    }

    return 0;
  }

  /**
   * Score catastral 0-10 (FC.06a): compara uso DGC del inmueble Catastro
   * con la tipología esperada del candidato de referencia.
   *
   * 10 → uso_cod compatible con tipología
   *  5 → sin dato / código desconocido / campo vacío (neutral, no penaliza)
   *  0 → uso_cod incompatible con tipología
   */
  private scoreCatastral(
    infra: ProcessedInfrastructure,
    cand: CandidatoReferencia,
  ): number {
    const parcel = infra.catastroParcel;
    if (!parcel) return Math.round(W_CATASTRAL * 0.5); // sin dato → 5 pts (neutral)

    const usoCod = parcel.uso_cod?.trim().toUpperCase() ?? '';
    if (!usoCod) return Math.round(W_CATASTRAL * 0.5); // campo vacío → neutro

    const compatibles = USO_COD_COMPATIBLE[usoCod];
    if (!compatibles) return Math.round(W_CATASTRAL * 0.5); // código desconocido → neutro

    return compatibles.includes(cand.tipologia) ? W_CATASTRAL : 0;
  }

  // ===========================================================================
  // ÍNDICE ESPACIAL
  // ===========================================================================

  private buildSpatialIndex(candidatos: CandidatoReferencia[]): Flatbush {
    const idx = new Flatbush(candidatos.length);
    for (const c of candidatos) {
      idx.add(c.x25830, c.y25830, c.x25830, c.y25830);
    }
    idx.finish();
    return idx;
  }

  private searchRadius(
    idx: Flatbush, candidatos: CandidatoReferencia[],
    cx: number, cy: number, radio: number,
  ): number[] {
    const boxIdxs = idx.search(cx - radio, cy - radio, cx + radio, cy + radio);
    const radioSq = radio * radio;
    return boxIdxs.filter(i => {
      const dx = candidatos[i].x25830 - cx;
      const dy = candidatos[i].y25830 - cy;
      return dx * dx + dy * dy <= radioSq;
    });
  }
}
